import React, { FC, useEffect, useState } from "react";
import { Pagination } from "@mui/material";
import styles from "./SendReminder.module.scss";
import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import SendReminderTable from "@features/sendReminder/sendReminderTable/SendReminderTable";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { selectDashboardData, notifyUser } from "@redux/slices/CommonSlice";
import {
  useFetchExpiryReminderHistoryQuery,
  useSendExpiryReminderMutation,
} from "@features/sendReminder/sendReminderAPI";
import { useFetchPromotionsListQuery } from "@features/issueCredits/issueCreditsAPI";

const SendReminder: FC = () => {
  // ============================================================================
  // Redux Hooks
  // ============================================================================
  const dispatch = useAppDispatch();
  const dashboardData = useAppSelector(selectDashboardData);

  // ============================================================================
  // Local State
  // ============================================================================
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPromo, setSelectedPromo] = useState<any>(null);
  const [promoName, setPromoName] = useState("");
  const [promotions, setPromotions] = useState<any[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [currentFetchUrl, setCurrentFetchUrl] = useState<string | null>(null);
  const [promoSearchTerm, setPromoSearchTerm] = useState("");

  // ============================================================================
  // API Hooks
  // ============================================================================
  const { data, isLoading, isFetching, refetch } =
    useFetchExpiryReminderHistoryQuery({
      page: currentPage,
      ...(searchTerm && { keyword: searchTerm }),
      ...(selectedPromo && { promotions: selectedPromo }),
    });

  const {
    data: initialData,
    refetch: refetchPromotions,
    isLoading: isLoadingPromotions,
  } = useFetchPromotionsListQuery({
    ...(promoSearchTerm && { keyword: promoSearchTerm }),
  });

  const { data: nextPageData, isFetching: isFetchingNextPage } =
    useFetchPromotionsListQuery(
      currentFetchUrl ?  currentFetchUrl  : undefined,
      {
        skip: !currentFetchUrl,
      }
    );

  const [sendReminder, { isLoading: isSending }] =
    useSendExpiryReminderMutation();

  // ==========================================================================
  // Effects
  // ==========================================================================
  /**
   * Load initial promotions data
   */
  useEffect(() => {
    if (initialData?.data) {
      setPromotions(initialData.data);
      setNextPageUrl(initialData.next || null);
    }
  }, [initialData]);

  /**
   * Handle pagination - append next page data
   */
  useEffect(() => {
    if (nextPageData?.data && currentFetchUrl) {
      setPromotions((prev) => [...prev, ...nextPageData.data]);
      setNextPageUrl(nextPageData.next || null);
      setCurrentFetchUrl(null);
    }
  }, [nextPageData, currentFetchUrl]);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

 const handlePromotionSelect = (value: string,name?:string) => {
    setSelectedPromo(value);
    setPromoName(name || "");
  };

  const handlePromotionSearchChange = (value: string) => {
    setPromoSearchTerm(value);
  };

  const handleSendReminder = async (credit_id: number) => {
    try {
      await sendReminder({ credit_id }).unwrap();

      dispatch(
        notifyUser({
          message: "Your Reminder has been successfully sent",
          title: "Reminder Sent",
          severity: "success",
        })
      );

      // Refetch the data to update the table
      refetch();
    } catch (error: any) {
      dispatch(
        notifyUser({
          message: error?.data?.message || "Please contact support.",
          title: "Reminder Failed",
          severity: "error",
        })
      );
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={styles["page-container"]}>
      <LayoutHeader
        currency={dashboardData?.currency}
        amount={dashboardData?.amount}
        headerTitle="Send Reminder"
      />

      <SendReminderTable
        data={data?.data}
        isLoading={isLoading || isFetching}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        selectedPromo={selectedPromo}
        onPromoSelected={handlePromotionSelect}
        onSendReminder={handleSendReminder}
        promotions={promotions}
        nextPageUrl={nextPageUrl}
        isFetchingNextPage={isFetchingNextPage}
        setCurrentFetchUrl={setCurrentFetchUrl}
        onPromoSearchChange={handlePromotionSearchChange}
        promoSearchTerm={promoSearchTerm}
        promoName={promoName}
      />

      {/* Pagination */}
      {data && data.total_page > 1 && (
        <div className={styles["pagination-container"]}>
          <span className={styles["pagination-container__rows-per-page"]}>
            Rows Per Page: {data?.current_page_count || 0}
          </span>
          <Pagination
            count={data.total_page}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            shape="rounded"
            className="compact-pagination"
          />
          <span className={styles["pagination-container__total-pages"]}>
            of {data.total_page} pages
          </span>
        </div>
      )}
    </div>
  );
};

export default SendReminder;
